import { CanceledError } from "axios";

import type {
  ICreateUploadLimiterProps,
  IRunLimitedUploadProps,
  IUploadLimiter,
} from "../types/uploads";

// NOTE: NSAI-7817 — runs at most `maxConcurrent` uploads at once and starts the
// rest in the order they were asked for. A batch picked together used to be
// sent all at once, so every file sat in the backend's memory at the same time.
// Isomorphic (no DOM, no timers), so web and mobile share one implementation.
export const createUploadLimiter = ({
  maxConcurrent,
}: ICreateUploadLimiterProps): IUploadLimiter => {
  const limit = Math.max(1, Math.floor(maxConcurrent));
  let running = 0;
  const waiting: Array<() => void> = [];

  // NOTE: A finished upload hands its slot straight to the next waiter instead
  // of freeing it. Freeing it first would let a caller arriving before the
  // waiter resumes (a microtask later) take the slot too, one over the limit.
  const release = (): void => {
    const next = waiting.shift();
    if (next) {
      next();
      return;
    }
    running -= 1;
  };

  const acquire = (signal: AbortSignal | undefined): Promise<void> => {
    if (running < limit) {
      running += 1;
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const start = (): void => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      };
      const onAbort = (): void => {
        const index = waiting.indexOf(start);
        if (index !== -1) {
          waiting.splice(index, 1);
        }
        reject(new CanceledError("Upload cancelled before it started"));
      };
      if (signal?.aborted) {
        onAbort();
        return;
      }
      signal?.addEventListener("abort", onAbort, { once: true });
      waiting.push(start);
    });
  };

  const run = async <TResult>({
    task,
    signal,
    onStart,
  }: IRunLimitedUploadProps<TResult>): Promise<TResult> => {
    await acquire(signal);
    try {
      onStart?.();
      return await task();
    } finally {
      release();
    }
  };

  return { run };
};
